/** A kit Button that navigates with react-router (keeps the `<a href>` semantics for middle-click / SR). */
import { forwardRef, type MouseEvent } from 'react';
import { useHref, useNavigate } from 'react-router';
import { Button, type LinkButtonProps } from '@windrop/ui';

export interface RouterButtonProps extends Omit<LinkButtonProps, 'href'> {
  to: string;
  /** Replace the history entry instead of pushing. */
  replace?: boolean;
}

export const LinkButton = forwardRef<HTMLAnchorElement, RouterButtonProps>(function LinkButton(
  { to, replace, onClick, ...rest },
  ref,
) {
  const navigate = useNavigate();
  const href = useHref(to);
  const isExternal = /^[a-z]+:/i.test(to);
  return (
    <Button
      ref={ref as never}
      href={isExternal ? to : href}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        if (e.defaultPrevented || isExternal) return;
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        void navigate(to, { replace });
      }}
      {...rest}
    />
  );
});
